/**
 * ConhecimentoPanel.jsx — Rê Leve
 * Aba "Conhecimento" da Janela do Operador.
 * 4 frames editáveis: RAG · Guardrails · Contexto · Aprendizados.
 * + Base nutricional ampla (consulta).
 */

import { useEffect, useMemo, useState } from 'react';
import { subscribeAll } from '../lib/liveSyncEngine';
import {
  getRAG, addRAG, updateRAG, deleteRAG,
  getGuardrails, updateGuardrail, addGuardrail,
  getContext, updateContext, addContext, deleteContext,
  getLearned, deleteLearned, addManualLearned,
  exportAll,
} from '../lib/knowledgeStoreEngine';
import { NUTRITION_DB, getGroups, getByGroup } from '../lib/nutritionDatabase';
import { getCustomFoods, addCustomFood, deleteCustomFood } from '../lib/customNutrition';
import { getAssistantName, setAssistantName, NAME_SUGGESTIONS } from '../lib/assistantConfig';

const FRAMES = [
  { id: 'rag',         label: 'RAG · Base de conhecimento', icon: '📚', desc: 'O que a Léve consulta antes de responder' },
  { id: 'guardrails',  label: 'Guardrails',                 icon: '🛡️', desc: 'Regras que vencem o LLM, sem exceção' },
  { id: 'context',     label: 'Contexto da empresa',        icon: '🏢', desc: 'Dados de negócio que a Léve usa em todas as respostas' },
  { id: 'learned',     label: 'Aprendizados',               icon: '🌱', desc: 'O que a equipe ensinou após perguntas pendentes' },
  { id: 'nutrition',   label: 'Base nutricional',           icon: '🥗', desc: '80+ alimentos para suporte empírico' },
];

export default function ConhecimentoPanel() {
  const [active, setActive] = useState('rag');
  const [tick, setTick] = useState(0);
  const refresh = () => setTick(t => t + 1);

  // Re-render automático em qualquer mudança vinda de outras abas (live sync)
  useEffect(() => {
    const unsub = subscribeAll((evt) => {
      if (evt.type.startsWith('knowledge.')) setTick(t => t + 1);
    });
    return () => unsub();
  }, []);

  function downloadJSON() {
    const data = exportAll();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `releve-conhecimento-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="rl-ops-panel">
      <div className="rl-ops-header">
        <div>
          <div className="rl-ops-eyebrow">
            Conhecimento · base viva da {getAssistantName()}
            <span className="rl-editable-badge">✏️ tudo editável · salvo localmente</span>
          </div>
          <h1 className="rl-ops-title">O cérebro da {getAssistantName()}, totalmente editável</h1>
          <p className="rl-ops-sub">
            Tudo aqui é <strong>real e editável</strong>. Clique nos "✏️ editar" / "+ novo" para alterar.
            Salvamento imediato (localStorage agora; sincroniza com Postgres quando o backend ativar).
            Auditável no log da sessão.
          </p>
        </div>
        <div className="rl-ops-actions">
          <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={downloadJSON}>⇩ Backup JSON</button>
          <button className="rl-ops-refresh" onClick={refresh}>↻ Atualizar</button>
        </div>
      </div>

      <div className="rl-frame-tabs">
        {FRAMES.map(f => (
          <button
            key={f.id}
            onClick={() => setActive(f.id)}
            className={'rl-frame-tab' + (active === f.id ? ' rl-frame-tab--active' : '')}
          >
            <span className="rl-frame-tab-icon">{f.icon}</span>
            <div>
              <div className="rl-frame-tab-label">{f.label}</div>
              <div className="rl-frame-tab-desc">{f.desc}</div>
            </div>
          </button>
        ))}
      </div>

      {active === 'rag'        && <RAGFrame onChange={refresh} />}
      {active === 'guardrails' && <GuardrailsFrame onChange={refresh} />}
      {active === 'context'    && <ContextFrame onChange={refresh} />}
      {active === 'learned'    && <LearnedFrame onChange={refresh} />}
      {active === 'nutrition'  && <NutritionFrame />}
    </div>
  );
}

// ── RAG ────────────────────────────────────────────────────────────────────
function RAGFrame({ onChange }) {
  const items = getRAG();
  const [editing, setEditing] = useState(null);
  const [newE, setNewE] = useState({ topic: '', text: '', tags: '', priority: 'média' });
  const [showNew, setShowNew] = useState(false);

  function saveNew() {
    if (!newE.topic.trim() || !newE.text.trim()) return;
    addRAG({
      topic: newE.topic.trim(),
      text: newE.text.trim(),
      tags: newE.tags.split(',').map(s => s.trim()).filter(Boolean),
      priority: newE.priority,
    });
    setNewE({ topic: '', text: '', tags: '', priority: 'média' });
    setShowNew(false);
    onChange();
  }

  function saveEdit() {
    if (!editing) return;
    updateRAG(editing.id, {
      topic: editing.topic,
      text: editing.text,
      tags: typeof editing.tags === 'string'
        ? editing.tags.split(',').map(s => s.trim()).filter(Boolean)
        : editing.tags,
      priority: editing.priority,
    });
    setEditing(null);
    onChange();
  }

  return (
    <section className="rl-card">
      <div className="rl-card-head">
        <div>
          <div className="rl-card-eyebrow">{items.length} entradas no RAG</div>
          <div className="rl-card-title">📚 Base de conhecimento</div>
        </div>
        <div className="rl-card-actions">
          <button className="rl-btn rl-btn--small" onClick={() => setShowNew(true)}>+ Nova entrada</button>
        </div>
      </div>

      {showNew && (
        <div className="rl-edit-form">
          <input className="rl-input" placeholder="Tópico (ex: horário de entrega)"
            value={newE.topic} onChange={e => setNewE({...newE, topic: e.target.value})} />
          <textarea className="rl-input rl-input--text" rows={3} placeholder="Texto que a Léve vai consultar..."
            value={newE.text} onChange={e => setNewE({...newE, text: e.target.value})} />
          <input className="rl-input" placeholder="Tags separadas por vírgula"
            value={newE.tags} onChange={e => setNewE({...newE, tags: e.target.value})} />
          <select className="rl-input" value={newE.priority} onChange={e => setNewE({...newE, priority: e.target.value})}>
            <option value="alta">Prioridade alta</option>
            <option value="média">Prioridade média</option>
            <option value="baixa">Prioridade baixa</option>
          </select>
          <div className="rl-form-actions">
            <button className="rl-btn rl-btn--small" onClick={saveNew}>✓ Salvar</button>
            <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={() => setShowNew(false)}>Cancelar</button>
          </div>
        </div>
      )}

      <div className="rl-list">
        {items.map(e => (
          <div key={e.id} className="rl-rag-entry">
            {editing?.id === e.id ? (
              <div className="rl-edit-form">
                <input className="rl-input" value={editing.topic}
                  onChange={ev => setEditing({...editing, topic: ev.target.value})} />
                <textarea className="rl-input rl-input--text" rows={3} value={editing.text}
                  onChange={ev => setEditing({...editing, text: ev.target.value})} />
                <input className="rl-input" placeholder="Tags"
                  value={Array.isArray(editing.tags) ? editing.tags.join(', ') : editing.tags}
                  onChange={ev => setEditing({...editing, tags: ev.target.value})} />
                <select className="rl-input" value={editing.priority}
                  onChange={ev => setEditing({...editing, priority: ev.target.value})}>
                  <option value="alta">Alta</option>
                  <option value="média">Média</option>
                  <option value="baixa">Baixa</option>
                </select>
                <div className="rl-form-actions">
                  <button className="rl-btn rl-btn--small" onClick={saveEdit}>✓ Salvar</button>
                  <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={() => setEditing(null)}>Cancelar</button>
                </div>
              </div>
            ) : (
              <>
                <div className="rl-rag-head">
                  <div className="rl-rag-topic">{e.topic}</div>
                  <span className={'rl-rag-priority rl-rag-priority--' + e.priority}>{e.priority}</span>
                  {e.source !== 'seed' && <span className="rl-rag-source">{e.source}</span>}
                </div>
                <div className="rl-rag-text">{e.text}</div>
                {e.tags && e.tags.length > 0 && (
                  <div className="rl-rag-tags">
                    {e.tags.map(t => <span key={t} className="rl-tag">#{t}</span>)}
                  </div>
                )}
                <div className="rl-rag-actions">
                  <button className="rl-link" onClick={() => setEditing({...e, tags: (e.tags || []).join(', ')})}>✏️ Editar</button>
                  <button className="rl-link rl-link--danger" onClick={() => { if (confirm('Remover esta entrada?')) { deleteRAG(e.id); onChange(); } }}>🗑️ Remover</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Guardrails ─────────────────────────────────────────────────────────────
function GuardrailsFrame({ onChange }) {
  const items = getGuardrails();
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newG, setNewG] = useState({ name: '', rule: '', severity: 'média' });

  function toggle(id, active) {
    updateGuardrail(id, { active: !active });
    onChange();
  }

  function saveEdit() {
    if (!editing) return;
    updateGuardrail(editing.id, {
      name: editing.name,
      rule: editing.rule,
      severity: editing.severity,
    });
    setEditing(null);
    onChange();
  }

  function saveNew() {
    if (!newG.name.trim() || !newG.rule.trim()) return;
    addGuardrail({ ...newG, active: true });
    setNewG({ name: '', rule: '', severity: 'média' });
    setShowNew(false);
    onChange();
  }

  return (
    <section className="rl-card">
      <div className="rl-card-head">
        <div>
          <div className="rl-card-eyebrow">{items.filter(g => g.active).length}/{items.length} ativos · todos editáveis</div>
          <div className="rl-card-title">🛡️ Guardrails</div>
        </div>
        <div className="rl-card-actions">
          <button className="rl-btn rl-btn--small" onClick={() => setShowNew(s => !s)}>+ Novo guardrail</button>
        </div>
      </div>

      {showNew && (
        <div className="rl-edit-form">
          <label className="rl-form-label">Nome curto</label>
          <input className="rl-input" value={newG.name}
            onChange={e => setNewG({ ...newG, name: e.target.value })}
            placeholder="ex: Não recomendar dieta cetogênica" />
          <label className="rl-form-label">Regra completa</label>
          <textarea className="rl-input rl-input--text" rows={3} value={newG.rule}
            onChange={e => setNewG({ ...newG, rule: e.target.value })}
            placeholder="ex: Bot nunca sugere protocolo cetogênico. Encaminha para nutricionista parceira." />
          <select className="rl-input" value={newG.severity}
            onChange={e => setNewG({ ...newG, severity: e.target.value })}>
            <option value="crítica">Crítica</option>
            <option value="alta">Alta</option>
            <option value="média">Média</option>
          </select>
          <div className="rl-form-actions">
            <button className="rl-btn rl-btn--small" onClick={saveNew}>✓ Salvar</button>
            <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={() => setShowNew(false)}>Cancelar</button>
          </div>
        </div>
      )}

      <div className="rl-list">
        {items.map(g => (
          <div key={g.id} className={'rl-guard rl-guard--' + g.severity + (g.active ? '' : ' rl-guard--off')}>
            {editing?.id === g.id ? (
              <div className="rl-edit-form">
                <input className="rl-input" value={editing.name}
                  onChange={e => setEditing({...editing, name: e.target.value})} />
                <textarea className="rl-input rl-input--text" rows={3} value={editing.rule}
                  onChange={e => setEditing({...editing, rule: e.target.value})} />
                <select className="rl-input" value={editing.severity}
                  onChange={e => setEditing({...editing, severity: e.target.value})}>
                  <option value="crítica">Crítica</option>
                  <option value="alta">Alta</option>
                  <option value="média">Média</option>
                </select>
                <div className="rl-form-actions">
                  <button className="rl-btn rl-btn--small" onClick={saveEdit}>✓ Salvar</button>
                  <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={() => setEditing(null)}>Cancelar</button>
                </div>
              </div>
            ) : (
              <>
                <div className="rl-guard-head">
                  <span className="rl-guard-code">{g.id}</span>
                  <span className="rl-guard-name">{g.name}</span>
                  <span className={'rl-guard-sev rl-guard-sev--' + g.severity}>{g.severity}</span>
                  <label className="rl-switch">
                    <input type="checkbox" checked={g.active} onChange={() => toggle(g.id, g.active)} />
                    <span className="rl-switch-slider"></span>
                  </label>
                </div>
                <div className="rl-guard-rule">{g.rule}</div>
                <div className="rl-rag-actions">
                  <button className="rl-link" onClick={() => setEditing(g)}>✏️ Editar</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Context ────────────────────────────────────────────────────────────────
function ContextFrame({ onChange }) {
  const items = getContext();
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [assistantName, setLocalName] = useState(getAssistantName());

  function startEdit(c) {
    setEditingId(c.id);
    setEditingValue(c.value);
  }
  function saveEdit() {
    updateContext(editingId, editingValue);
    setEditingId(null);
    onChange();
  }
  function applyName(name) {
    setAssistantName(name);
    setLocalName(name);
    onChange();
  }

  return (
    <>
      {/* Nome da assistente — destaque */}
      <section className="rl-card rl-card--mostard" style={{ marginBottom: 18 }}>
        <div className="rl-card-head">
          <span className="rl-leve-avatar">🌱</span>
          <div>
            <div className="rl-card-eyebrow">Identidade da assistente</div>
            <div className="rl-card-title">Como o cliente chama a assistente da Rê Leve?</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="rl-input"
            style={{ flex: '1 1 200px', marginBottom: 0, fontSize: 16, fontWeight: 700 }}
            value={assistantName}
            onChange={e => setLocalName(e.target.value)}
            onBlur={e => applyName(e.target.value)}
            placeholder="Nome da assistente"
          />
          <button className="rl-btn rl-btn--small" onClick={() => applyName(assistantName)}>✓ Salvar nome</button>
        </div>
        <div style={{ marginTop: 12 }}>
          <div className="caps-label" style={{ marginBottom: 6 }}>Sugestões que combinam com Rê Leve:</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {NAME_SUGGESTIONS.map(s => (
              <button
                key={s.name}
                onClick={() => applyName(s.name)}
                className={'rl-chip ' + (assistantName === s.name ? 'rl-chip--active' : '')}
                title={s.reason}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--rl-ink-2)', marginTop: 12, lineHeight: 1.5 }}>
          O nome é aplicado em <strong>todos os lugares</strong> automaticamente: header do chat,
          mensagens da assistente, painel do operador, notificações. Salvo localmente.
        </p>
      </section>

      {/* Demais variáveis */}
      <ContextVariables onChange={onChange} />
    </>
  );
}

function ContextVariables({ onChange }) {
  const items = getContext();
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newC, setNewC] = useState({ key: '', value: '' });

  function startEdit(c) {
    setEditingId(c.id);
    setEditingValue(c.value);
  }
  function saveEdit() {
    updateContext(editingId, editingValue);
    setEditingId(null);
    onChange();
  }
  function saveNew() {
    if (!newC.key.trim() || !newC.value.trim()) return;
    addContext(newC);
    setNewC({ key: '', value: '' });
    setShowNew(false);
    onChange();
  }

  return (
      <section className="rl-card">
        <div className="rl-card-head">
          <div>
            <div className="rl-card-eyebrow">{items.length} variáveis editáveis</div>
            <div className="rl-card-title">🏢 Contexto da empresa</div>
          </div>
          <div className="rl-card-actions">
            <span className="rl-editable-badge">✏️ tudo editável</span>
            <button className="rl-btn rl-btn--small" onClick={() => setShowNew(s => !s)}>+ Nova variável</button>
          </div>
        </div>

        {showNew && (
          <div className="rl-edit-form">
            <label className="rl-form-label">Nome da variável</label>
            <input className="rl-input" value={newC.key}
              onChange={e => setNewC({ ...newC, key: e.target.value })}
              placeholder="ex: tempo médio de entrega" />
            <label className="rl-form-label">Valor</label>
            <input className="rl-input" value={newC.value}
              onChange={e => setNewC({ ...newC, value: e.target.value })}
              placeholder="ex: 25 minutos em Boa Viagem" />
            <div className="rl-form-actions">
              <button className="rl-btn rl-btn--small" onClick={saveNew}>✓ Salvar</button>
              <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={() => setShowNew(false)}>Cancelar</button>
            </div>
          </div>
        )}
        <table className="rl-context-table">
          <tbody>
            {items.map(c => (
              <tr key={c.id}>
                <td className="rl-context-key">{c.key}</td>
                <td className="rl-context-value">
                  {editingId === c.id ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input className="rl-input" value={editingValue}
                        onChange={e => setEditingValue(e.target.value)} autoFocus />
                      <button className="rl-btn rl-btn--small" onClick={saveEdit}>✓</button>
                      <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={() => setEditingId(null)}>×</button>
                    </div>
                  ) : (
                    <>
                      <span>{c.value}</span>
                      {c.editable !== false && (
                        <>
                          <button className="rl-link" style={{ marginLeft: 12 }} onClick={() => startEdit(c)}>✏️ editar</button>
                          <button className="rl-link rl-link--danger" style={{ marginLeft: 8 }}
                            onClick={() => { if (confirm('Remover esta variável?')) { deleteContext(c.id); onChange(); } }}>🗑</button>
                        </>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
  );
}

// ── Learned ────────────────────────────────────────────────────────────────
function LearnedFrame({ onChange }) {
  const items = getLearned();
  const [showNew, setShowNew] = useState(false);
  const [newL, setNewL] = useState({ question: '', answer: '', tags: '' });

  function saveNew() {
    if (!newL.question.trim() || !newL.answer.trim()) return;
    addManualLearned({
      question: newL.question.trim(),
      answer: newL.answer.trim(),
      tags: newL.tags.split(',').map(s => s.trim()).filter(Boolean),
    });
    setNewL({ question: '', answer: '', tags: '' });
    setShowNew(false);
    onChange();
  }

  return (
    <section className="rl-card">
      <div className="rl-card-head">
        <div>
          <div className="rl-card-eyebrow">{items.length} aprendizados (aprovações + manual)</div>
          <div className="rl-card-title">🌱 O que a equipe ensinou pra Léve</div>
        </div>
        <div className="rl-card-actions">
          <button className="rl-btn rl-btn--small" onClick={() => setShowNew(s => !s)}>+ Ensinar manualmente</button>
        </div>
      </div>

      {showNew && (
        <div className="rl-edit-form">
          <label className="rl-form-label">Pergunta típica do cliente</label>
          <input className="rl-input" value={newL.question}
            onChange={e => setNewL({ ...newL, question: e.target.value })}
            placeholder="ex: Vocês entregam em Olinda?" />
          <label className="rl-form-label">Resposta que a Léve deve dar</label>
          <textarea className="rl-input rl-input--text" rows={3} value={newL.answer}
            onChange={e => setNewL({ ...newL, answer: e.target.value })}
            placeholder="ex: Sim! Atendemos Bairro Novo e Casa Caiada. Outras regiões de Olinda a gente tá expandindo." />
          <label className="rl-form-label">Tags (separadas por vírgula)</label>
          <input className="rl-input" value={newL.tags}
            onChange={e => setNewL({ ...newL, tags: e.target.value })}
            placeholder="ex: entrega, Olinda, bairros" />
          <div className="rl-form-actions">
            <button className="rl-btn rl-btn--small" onClick={saveNew}>✓ Ensinar</button>
            <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={() => setShowNew(false)}>Cancelar</button>
          </div>
        </div>
      )}
      <div className="rl-list">
        {items.length === 0 && (
          <div className="rl-empty rl-empty--big">
            Nenhum aprendizado registrado ainda.<br/>
            Conforme a equipe responder perguntas pendentes na aba <strong>Aprovações</strong>, eles aparecem aqui.
          </div>
        )}
        {items.map(l => (
          <div key={l.id} className="rl-learned">
            <div className="rl-learned-q">"{l.fromQuestion}"</div>
            <div className="rl-learned-a">{l.answer}</div>
            {l.tags && l.tags.length > 0 && (
              <div className="rl-rag-tags">{l.tags.map(t => <span key={t} className="rl-tag">#{t}</span>)}</div>
            )}
            <div className="rl-learned-meta">
              <span>{l.customerSegment}</span>
              <span>{new Date(l.answeredAt || l.createdAt).toLocaleDateString('pt-BR')}</span>
              <button className="rl-link rl-link--danger" onClick={() => { if (confirm('Esquecer este aprendizado?')) { deleteLearned(l.id); onChange(); } }}>🗑️</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Nutrition (filtros dinâmicos + calculadora de porção) ─────────────────
function NutritionFrame() {
  const groups = getGroups();
  const [selectedGroups, setSelectedGroups] = useState(new Set()); // vazio = todos
  const [search, setSearch] = useState('');
  const [portion, setPortion] = useState(100);
  const [sortBy, setSortBy] = useState('name'); // name | kcal | protein

  function toggleGroup(g) {
    const next = new Set(selectedGroups);
    if (next.has(g)) next.delete(g); else next.add(g);
    setSelectedGroups(next);
  }

  const data = useMemo(() => {
    const q = search.toLowerCase().trim();
    let arr = [...NUTRITION_DB, ...getCustomFoods()];
    if (selectedGroups.size > 0) {
      arr = arr.filter(f => selectedGroups.has(f.group));
    }
    if (q) {
      arr = arr.filter(f =>
        f.name.toLowerCase().includes(q) ||
        (f.aliases || []).some(a => a.toLowerCase().includes(q))
      );
    }
    // Aplicar porção (multiplica todos os valores)
    const factor = portion / 100;
    arr = arr.map(f => ({
      ...f,
      kcalP:    Math.round(f.kcal    * factor * 10) / 10,
      proteinP: Math.round(f.protein * factor * 10) / 10,
      carbP:    Math.round(f.carb    * factor * 10) / 10,
      fatP:     Math.round(f.fat     * factor * 10) / 10,
      fiberP:   Math.round(f.fiber   * factor * 10) / 10,
    }));
    // Sort
    if (sortBy === 'kcal')    arr.sort((a, b) => b.kcalP - a.kcalP);
    if (sortBy === 'protein') arr.sort((a, b) => b.proteinP - a.proteinP);
    if (sortBy === 'name')    arr.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    return arr;
  }, [search, selectedGroups, portion, sortBy]);

  const isFiltered = search || selectedGroups.size > 0;

  return (
    <section className="rl-card">
      <div className="rl-card-head">
        <div>
          <div className="rl-card-eyebrow">
            {NUTRITION_DB.length} padrão + {getCustomFoods().length} personalizados · {data.length} {isFiltered ? 'filtrados' : 'no total'} ·
            valores para <strong>{portion}g/ml</strong>
          </div>
          <div className="rl-card-title">🥗 Base nutricional</div>
        </div>
        <div className="rl-card-actions">
          <NewFoodButton onAdded={() => setSortBy(s => s)} />
        </div>
      </div>

      {/* Linha de filtros dinâmicos */}
      <div className="rl-nutri-filters">
        <input
          className="rl-input"
          style={{ marginBottom: 0, flex: '2 1 220px' }}
          placeholder="🔍 Buscar (ex: salmão, mandioquinha, açaí, banana...)"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="rl-portion-calc">
          <label>Porção:</label>
          <input
            type="number"
            min="1"
            max="2000"
            step="10"
            value={portion}
            onChange={e => setPortion(Number(e.target.value) || 100)}
            className="rl-input"
            style={{ width: 80, marginBottom: 0 }}
          />
          <span className="rl-portion-unit">g / ml</span>
          <div className="rl-portion-presets">
            {[50, 100, 150, 200, 250].map(p => (
              <button key={p} onClick={() => setPortion(p)}
                className={'rl-chip rl-chip--mini ' + (portion === p ? 'rl-chip--active' : '')}>{p}g</button>
            ))}
          </div>
        </div>
      </div>

      <div className="rl-nutri-filters" style={{ marginTop: 10 }}>
        <div className="rl-group-tabs" style={{ flex: 1, margin: 0 }}>
          <button
            onClick={() => setSelectedGroups(new Set())}
            className={'rl-chip ' + (selectedGroups.size === 0 ? 'rl-chip--active' : '')}>todos</button>
          {groups.map(g => (
            <button key={g}
              onClick={() => toggleGroup(g)}
              className={'rl-chip ' + (selectedGroups.has(g) ? 'rl-chip--active' : '')}>
              {g}
            </button>
          ))}
        </div>
        <div className="rl-sort-by">
          <span style={{ fontSize: 11, color: 'var(--rl-ink-2)', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>ordenar:</span>
          <button onClick={() => setSortBy('name')}    className={'rl-chip rl-chip--mini ' + (sortBy === 'name' ? 'rl-chip--active' : '')}>A→Z</button>
          <button onClick={() => setSortBy('kcal')}    className={'rl-chip rl-chip--mini ' + (sortBy === 'kcal' ? 'rl-chip--active' : '')}>+kcal</button>
          <button onClick={() => setSortBy('protein')} className={'rl-chip rl-chip--mini ' + (sortBy === 'protein' ? 'rl-chip--active' : '')}>+proteína</button>
        </div>
      </div>

      <div style={{ overflowX: 'auto', marginTop: 14 }}>
        <table className="rl-nutrition-table">
          <thead>
            <tr>
              <th>Alimento</th>
              <th>Grupo</th>
              <th style={{ textAlign: 'right' }}>kcal</th>
              <th style={{ textAlign: 'right' }}>Proteína</th>
              <th style={{ textAlign: 'right' }}>Carbo</th>
              <th style={{ textAlign: 'right' }}>Gordura</th>
              <th style={{ textAlign: 'right' }}>Fibra</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr><td colSpan={7}><div className="rl-empty">Nenhum alimento corresponde ao filtro. Tente outro termo.</div></td></tr>
            )}
            {data.map(f => (
              <tr key={f.name}>
                <td className="rl-buy-name">{f.name}</td>
                <td><span className="rl-tag" style={{ fontSize: 10 }}>{f.group}</span></td>
                <td style={{ textAlign: 'right' }} className="rl-buy-qty">{f.kcalP}</td>
                <td style={{ textAlign: 'right' }}>{f.proteinP}g</td>
                <td style={{ textAlign: 'right' }}>{f.carbP}g</td>
                <td style={{ textAlign: 'right' }}>{f.fatP}g</td>
                <td style={{ textAlign: 'right' }}>{f.fiberP}g</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rl-nutri-footer">
        💡 Léve consulta essa base quando o cliente pergunta sobre alimentos fora dos kits.
        A porção é editável aqui — útil pra responder "200g de frango tem quanto?". Fontes: TACO/UNICAMP + USDA FoodData Central (médias). Para precisão clínica, o guardrail G03 sempre encaminha para nutricionista.
        Você pode <strong>adicionar alimentos próprios</strong> usando "+ Novo alimento" — eles aparecem misturados com a base padrão.
      </div>
    </section>
  );
}

function NewFoodButton({ onAdded }) {
  const [showNew, setShowNew] = useState(false);
  const [newF, setNewF] = useState({ name: '', aliases: '', kcal: '', protein: '', carb: '', fat: '', fiber: '', group: 'outro' });

  function save() {
    if (!newF.name.trim()) return;
    try {
      addCustomFood(newF);
      setNewF({ name: '', aliases: '', kcal: '', protein: '', carb: '', fat: '', fiber: '', group: 'outro' });
      setShowNew(false);
      onAdded && onAdded();
    } catch (e) {
      alert(e.message);
    }
  }

  if (!showNew) {
    return <button className="rl-btn rl-btn--small" onClick={() => setShowNew(true)}>+ Novo alimento</button>;
  }

  return (
    <div className="rl-edit-form" style={{ minWidth: 320 }}>
      <label className="rl-form-label">Nome</label>
      <input className="rl-input" value={newF.name}
        onChange={e => setNewF({ ...newF, name: e.target.value })}
        placeholder="ex: Bolinho de bacalhau" />
      <label className="rl-form-label">Aliases (separados por vírgula)</label>
      <input className="rl-input" value={newF.aliases}
        onChange={e => setNewF({ ...newF, aliases: e.target.value })}
        placeholder="bolinho, pataniscas" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
        <div>
          <label className="rl-form-label">kcal</label>
          <input className="rl-input" type="number" value={newF.kcal}
            onChange={e => setNewF({ ...newF, kcal: e.target.value })} />
        </div>
        <div>
          <label className="rl-form-label">Prot (g)</label>
          <input className="rl-input" type="number" value={newF.protein}
            onChange={e => setNewF({ ...newF, protein: e.target.value })} />
        </div>
        <div>
          <label className="rl-form-label">Carb (g)</label>
          <input className="rl-input" type="number" value={newF.carb}
            onChange={e => setNewF({ ...newF, carb: e.target.value })} />
        </div>
        <div>
          <label className="rl-form-label">Gord (g)</label>
          <input className="rl-input" type="number" value={newF.fat}
            onChange={e => setNewF({ ...newF, fat: e.target.value })} />
        </div>
        <div>
          <label className="rl-form-label">Fibra (g)</label>
          <input className="rl-input" type="number" value={newF.fiber}
            onChange={e => setNewF({ ...newF, fiber: e.target.value })} />
        </div>
      </div>
      <label className="rl-form-label">Grupo</label>
      <select className="rl-input" value={newF.group}
        onChange={e => setNewF({ ...newF, group: e.target.value })}>
        <option value="proteína">Proteína</option>
        <option value="carboidrato">Carboidrato</option>
        <option value="vegetal">Vegetal</option>
        <option value="fruta">Fruta</option>
        <option value="laticínio">Laticínio</option>
        <option value="gordura">Gordura</option>
        <option value="tempero">Tempero</option>
        <option value="bebida">Bebida</option>
        <option value="doce">Doce</option>
        <option value="outro">Outro</option>
      </select>
      <div className="rl-form-actions">
        <button className="rl-btn rl-btn--small" onClick={save}>✓ Adicionar</button>
        <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={() => setShowNew(false)}>Cancelar</button>
      </div>
    </div>
  );
}
